import { useCallback, useEffect, useRef, useState } from "react";
import type {
  FamilyPortalSession,
  FamilyPurchaseOffer,
  FamilyPurchasePrice,
} from "@shared/types/familyPortal";
import { ApiError, familyApi } from "./api";
import { leaveTo } from "./nav";

/**
 * The self-serve buy flow (PORTAL-3; design + Swedish copy, PORTAL-5).
 *
 * Three surfaces, one contract - `FamilyPurchaseOffer.state`, the server's single
 * verdict on whether calling can be bought right now:
 *
 *   VideoCallingCard      on the dashboard: status, and the way in.
 *   PurchaseScreen        /buy - price, what it buys, hand-off to the provider.
 *   PurchaseReturnScreen  /buy/return - back from the provider; wait for the seat.
 *
 * ── The two rules this file exists to keep ──────────────────────────────────
 *
 * 1. NEVER OFFER A SECOND SEAT. One active seat per resident, whoever paid for
 *    it, so `already_active` is a full stop and not a discount. The client cannot
 *    be the guard - it renders a state that was true when it was fetched - so the
 *    server re-checks at checkout and every screen here is written to absorb that
 *    refusal by re-reading the offer rather than showing an error.
 *
 * 2. NEVER CLAIM A PAYMENT WE HAVE NOT SEEN. Coming back from the provider is a
 *    navigation, not a receipt; anybody can type the URL. The return screen
 *    reports what the ENTITLEMENT says (`already_active` = the webhook landed and
 *    the seat exists), never what the redirect implies.
 *
 * The reader is often elderly, on a phone, and worried about being charged twice.
 * Every state below therefore says what is true, what it costs, and what to do
 * next - "something went wrong" is never enough here.
 *
 * Which is also why this file is in SWEDISH (PORTAL-5). It shipped in English
 * behind the provider seam while the sign-in screens it follows were already
 * Swedish - so the one flow that spends a relative's money was the one flow they
 * could not necessarily read.
 */

/* ------------------------------------------------------------------ *
 * The offer
 * ------------------------------------------------------------------ */

type OfferState =
  | { status: "loading" }
  | { status: "ready"; offer: FamilyPurchaseOffer }
  /** The probe itself failed (offline, 5xx). Distinct from any verdict: the
   *  remedy is "try again", not "ask the care home". */
  | { status: "error" };

interface OfferHandle {
  state: OfferState;
  reload(): Promise<FamilyPurchaseOffer | null>;
}

/**
 * Read the purchase offer, with a `reload` the screens call after a refused
 * checkout (the world moved) and after returning from payment (the seat may now
 * exist).
 *
 * A 401 is not handled here: the App's `/session` probe owns "you are signed
 * out", and duplicating that decision in two places is how a portal ends up
 * half-signed-out. This surfaces it as `error`; the next session probe redirects.
 */
export function useOffer(): OfferHandle {
  const [state, setState] = useState<OfferState>({ status: "loading" });
  const alive = useRef(true);
  /** Coalesces overlapping reads: the return screen polls while the hook is
   *  still doing its own mount fetch, and StrictMode runs that effect twice.
   *  One in-flight request answers all of them. */
  const inflight = useRef<Promise<FamilyPurchaseOffer | null> | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(async (): Promise<FamilyPurchaseOffer | null> => {
    if (inflight.current) return inflight.current;
    const request = (async () => {
      try {
        const offer = await familyApi.purchaseOffer();
        if (alive.current) setState({ status: "ready", offer });
        return offer;
      } catch {
        if (alive.current) setState({ status: "error" });
        return null;
      } finally {
        inflight.current = null;
      }
    })();
    inflight.current = request;
    return request;
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { state, reload };
}

/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */

/**
 * Format a minor-unit amount for the premise's locale.
 *
 * The exponent comes from `Intl` rather than a hard-coded /100: most currencies
 * we will ever quote have two decimals, but the one that does not (a zero-decimal
 * currency) would be rendered a hundred times too cheap, which is the single
 * worst rounding bug a purchase screen can have.
 */
export function formatPrice(
  price: FamilyPurchasePrice,
  locale: string
): string {
  try {
    const fmt = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: price.currency,
    });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(price.amount / 10 ** digits);
  } catch {
    // A currency code Intl does not know: still show a number rather than
    // nothing. Never silently drop the price - a purchase screen without one is
    // a screen asking for a blank cheque.
    return `${(price.amount / 100).toFixed(2)} ${price.currency}`;
  }
}

/** How a price is spoken: "per månad" / "engångskostnad". */
function intervalLabel(price: FamilyPurchasePrice): string {
  return price.interval === "month" ? "per månad" : "engångskostnad";
}

/** "99,00 kr per månad" / "99,00 kr engångskostnad" - the compact one-line form,
 *  for the dashboard card. The buy screen sets the amount and the interval on
 *  separate lines instead (see `Price`), because that is the screen where the
 *  number itself has to be unmissable. */
export function priceLabel(price: FamilyPurchasePrice, locale: string): string {
  return `${formatPrice(price, locale)} ${intervalLabel(price)}`;
}

/* ------------------------------------------------------------------ *
 * Small pieces
 * ------------------------------------------------------------------ */

/**
 * The price block on the buy screen: amount at heading size, interval beneath.
 *
 * Split across two lines on purpose. A relative scanning this screen is looking
 * for one thing - how much - and "99,00 kr per månad" as a single run of text
 * makes the number compete with its own qualifier. Both still read as one
 * statement to a screen reader, which is why they share a block rather than
 * being two paragraphs.
 */
function Price(props: { price: FamilyPurchasePrice; locale: string }) {
  return (
    <p className="price">
      <span className="price__amount">
        {formatPrice(props.price, props.locale)}
      </span>
      <span className="price__interval">{intervalLabel(props.price)}</span>
    </p>
  );
}

/**
 * The status check. Inline SVG rather than an asset so it inherits the premise's
 * accent, and `aria-hidden` because the word beside it carries the meaning -
 * that pairing is the whole point (never colour or icon alone).
 */
function CheckIcon(props: { className: string }) {
  return (
    <svg
      className={props.className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="10" cy="10" r="8.25" />
      <path d="M6.2 10.3 8.9 13l4.9-5.4" />
    </svg>
  );
}

/** "Active for X" - the one line that says a relative can reach their parent. */
function ActiveStatus(props: { resident: string; testId?: string }) {
  return (
    <p className="status" data-testid={props.testId}>
      <CheckIcon className="status__icon" />
      Aktivt för {props.resident}
    </p>
  );
}

/**
 * The dashboard card's heading, per verdict.
 *
 * It has to be state-driven, not one fixed line. A heading of "Ring {resident}
 * på TV:n" sitting above a body that says the care home has not switched calling
 * on is the screen promising something the paragraph under it takes away - and
 * the reader who believes the heading is the one who then rings the care home to
 * ask why it does not work.
 */
function cardHeading(state: FamilyPurchaseOffer["state"], resident: string) {
  switch (state) {
    case "already_active":
      return `Du kan ringa ${resident}`;
    case "available":
      return `Ring ${resident} på TV:n`;
    default:
      // Every state where nothing can be bought and nothing works yet. Says so.
      return "Inte igång än";
  }
}

/* ------------------------------------------------------------------ *
 * Dashboard card
 * ------------------------------------------------------------------ */

/** The resident's first name, or a neutral stand-in. The portal is allowed
 *  exactly this much about them (see FamilyPortalSession). */
function residentOf(session: FamilyPortalSession): string {
  return session.residentName || "your relative";
}

/**
 * "Video calling" on the dashboard. Every branch is a state the server named -
 * the card does not re-derive the verdict from `callingEnabled` /
 * `residentPresent` / `videoCallActive`, or the two would drift the first time
 * one of them changed between the two fetches.
 */
export function VideoCallingCard(props: {
  session: FamilyPortalSession;
  offer: OfferHandle;
  onBuy(): void;
}) {
  const { session, offer, onBuy } = props;
  const resident = residentOf(session);

  return (
    // The one raised, accent-capped panel on the dashboard: this card is what
    // the portal exists for, and before PORTAL-5 it looked exactly like the
    // sign-out button.
    <section className="card card--feature">
      <p className="eyebrow eyebrow--accent">Videosamtal</p>
      <h2 className="card__title">
        {offer.state.status === "ready"
          ? cardHeading(offer.state.offer.state, resident)
          : `Videosamtal med ${resident}`}
      </h2>
      {offer.state.status === "loading" ? (
        <p className="card__body" aria-busy="true">
          Kontrollerar…
        </p>
      ) : offer.state.status === "error" ? (
        <>
          <p className="card__body">
            Vi kunde inte hämta statusen för videosamtal just nu.
          </p>
          <button
            className="btn btn--ghost"
            type="button"
            onClick={() => void offer.reload()}
          >
            Försök igen
          </button>
        </>
      ) : (
        <CardBody
          state={offer.state.offer}
          resident={resident}
          premiseName={session.premiseName}
          locale={session.defaultLocale}
          onBuy={onBuy}
        />
      )}
    </section>
  );
}

function CardBody(props: {
  state: FamilyPurchaseOffer;
  resident: string;
  premiseName: string;
  locale: string;
  onBuy(): void;
}) {
  const { state, resident, premiseName, locale, onBuy } = props;

  switch (state.state) {
    // Nothing bought here would switch a tile on, so this is not a sales
    // opportunity - it is a redirection to the only people who can act.
    case "calling_disabled":
      return (
        <p className="card__body">
          {premiseName} har inte satt igång videosamtal än. Fråga gärna boendet
          om det — det finns ingenting att göra härifrån.
        </p>
      );
    case "no_active_stay":
      return (
        <p className="card__body">
          Det finns ingen pågående vistelse för {resident} just nu, så
          videosamtal är pausat.
        </p>
      );
    // Deliberately silent about WHO paid. A relative does not need to know that
    // the care home funds the seat, and saying so only invites "then cancel it".
    case "already_active":
      return (
        <>
          <ActiveStatus resident={resident} testId="calling-active" />
          <p className="card__body">
            Rutan för videosamtal finns på TV:n. Du ansluter via Foyer-länken
            som boendet har skickat till dig.
          </p>
        </>
      );
    case "available":
      return (
        <>
          <p className="card__body">
            Videosamtal är inte igång för {resident} än. Du kan sätta igång det
            härifrån.
          </p>
          {state.price ? (
            <p className="card__note">{priceLabel(state.price, locale)}</p>
          ) : null}
          <button className="btn btn--primary" type="button" onClick={onBuy}>
            Sätt igång videosamtal
          </button>
        </>
      );
    // We cannot take money right now (no provider configured - the whole estate
    // until PORTAL-4 lands). Say "not yet" rather than render a dead button.
    case "unavailable":
    default:
      return (
        <p className="card__body">
          Videosamtal är inte igång för {resident} än. Du kommer snart att kunna
          sätta igång det härifrån — eller be boendet slå på det åt dig.
        </p>
      );
  }
}

/* ------------------------------------------------------------------ *
 * /buy - the purchase screen
 * ------------------------------------------------------------------ */

type BuyState =
  | { status: "idle" }
  /** The provider is being asked for a hosted page; the browser is about to
   *  leave. Kept distinct from idle so the button cannot be pressed twice. */
  | { status: "starting" }
  | { status: "error"; message: string };

export function PurchaseScreen(props: {
  session: FamilyPortalSession;
  offer: OfferHandle;
  onDone(): void;
}) {
  const { session, offer, onDone } = props;
  const resident = residentOf(session);
  const [buy, setBuy] = useState<BuyState>({ status: "idle" });

  async function start() {
    if (buy.status === "starting") return;
    setBuy({ status: "starting" });
    try {
      const { url } = await familyApi.startCheckout();
      leaveTo(url);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 409 || status === 503) {
        // The server refused with the CURRENT state (already active, calling
        // switched off, stay ended, payments down). Re-read the offer and let
        // the screen re-render into that state: it is an answer, not an error.
        await offer.reload();
        setBuy({ status: "idle" });
        return;
      }
      setBuy({
        status: "error",
        message:
          "Vi kunde inte öppna betalsidan. Ingenting har debiterats — försök gärna igen.",
      });
    }
  }

  const state = offer.state;

  return (
    <section className="card card--feature">
      <p className="eyebrow eyebrow--accent">Videosamtal</p>
      <h1 className="card__title">Videosamtal för {resident}</h1>

      {state.status === "loading" ? (
        <p className="card__body" aria-busy="true">
          Kontrollerar…
        </p>
      ) : state.status === "error" ? (
        <>
          <p className="card__body">Vi kunde inte hämta uppgifterna just nu.</p>
          <button
            className="btn btn--ghost"
            type="button"
            onClick={() => void offer.reload()}
          >
            Försök igen
          </button>
        </>
      ) : state.offer.state === "available" && state.offer.price ? (
        <>
          <ul className="list">
            <li>
              <CheckIcon className="list__tick" />
              <span>
                {resident} kan ringa dig från sin egen TV, med fjärrkontrollen.
              </span>
            </li>
            <li>
              <CheckIcon className="list__tick" />
              <span>Du ansluter från din telefon, surfplatta eller dator.</span>
            </li>
            <li>
              <CheckIcon className="list__tick" />
              <span>
                Avslutas automatiskt om {resident} flyttar ut — ingenting att
                säga upp.
              </span>
            </li>
          </ul>
          <Price price={state.offer.price} locale={session.defaultLocale} />
          <button
            className="btn btn--primary"
            type="button"
            onClick={() => void start()}
            disabled={buy.status === "starting"}
          >
            {buy.status === "starting"
              ? "Öppnar betalning…"
              : "Fortsätt till betalning"}
          </button>
          <p className="card__note">
            Du betalar på vår betalleverantörs säkra sida. Vi ser aldrig dina
            kortuppgifter.
          </p>
          {buy.status === "error" ? (
            <p className="form__error" role="alert">
              {buy.message}
            </p>
          ) : null}
        </>
      ) : (
        // Every non-buyable state, including landing here directly: say which
        // one it is and offer the way back, never a disabled button with no
        // explanation.
        <>
          <CardBody
            state={state.offer}
            resident={resident}
            premiseName={session.premiseName}
            locale={session.defaultLocale}
            onBuy={() => undefined}
          />
        </>
      )}

      <button className="btn btn--link" type="button" onClick={onDone}>
        Tillbaka
      </button>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * /buy/return - back from the provider
 * ------------------------------------------------------------------ */

/** How long to wait for the webhook before saying "not yet". Six probes over
 *  ~15s: long enough for a provider callback on a normal day, short enough that
 *  a relative is not left watching a spinner. */
const POLL_INTERVAL_MS = 2500;
const POLL_ATTEMPTS = 6;

/**
 * The landing after payment.
 *
 * It reports the ENTITLEMENT, never the redirect. A relative who paid arrives
 * here possibly before the provider's webhook has reached us, so the screen waits
 * for the seat to appear - and if it does not, says so plainly instead of
 * claiming a success it cannot see or an error that would send a paying customer
 * to pay again.
 */
export function PurchaseReturnScreen(props: {
  session: FamilyPortalSession;
  offer: OfferHandle;
  /** Re-read the session so the dashboard behind this screen is current. */
  onActivated(): void;
  onDone(): void;
}) {
  const { session, offer, onActivated, onDone } = props;
  const resident = residentOf(session);
  const [waiting, setWaiting] = useState(true);
  const activated =
    offer.state.status === "ready" &&
    offer.state.offer.state === "already_active";

  useEffect(() => {
    if (!waiting) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const tick = async () => {
      const result = await offer.reload();
      if (cancelled) return;
      if (result?.state === "already_active") {
        setWaiting(false);
        onActivated();
        return;
      }
      attempts += 1;
      if (attempts >= POLL_ATTEMPTS) {
        setWaiting(false);
        return;
      }
      timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Deliberately keyed on `waiting` alone: `offer.reload` is stable, and
    // re-running this effect would restart the poll budget from zero.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  if (activated) {
    return (
      <section className="card card--feature">
        <p className="eyebrow eyebrow--accent">Klart</p>
        <h1 className="card__title">Videosamtal är igång</h1>
        <ActiveStatus resident={resident} />
        <p className="card__body">
          {resident} ser rutan för videosamtal på sin TV och kan ringa dig
          därifrån. Du ansluter via Foyer-länken som boendet har skickat till
          dig.
        </p>
        <button className="btn btn--link" type="button" onClick={onDone}>
          Tillbaka till {session.premiseName}
        </button>
      </section>
    );
  }

  if (waiting) {
    return (
      <section className="card card--feature" aria-busy="true">
        {/* A real spinner, not a bare line of text: this reader has just been
            charged and needs to see that something is happening. It says
            nothing about the outcome - the copy alone does that. */}
        <p className="eyebrow">Betalning</p>
        <div className="pulse" aria-hidden="true" />
        <h1 className="card__title">Snart klart…</h1>
        <p className="card__body" role="status">
          Vi väntar på att betalningen ska bekräftas. Det brukar ta några
          sekunder.
        </p>
      </section>
    );
  }

  // The budget ran out. We genuinely do not know whether money moved - this URL
  // is reachable by anyone - so the copy commits to neither, and above all does
  // not invite a second payment.
  return (
    <section className="card card--feature">
      <p className="eyebrow">Betalning</p>
      <h1 className="card__title">Inte bekräftat än</h1>
      <p className="card__body">
        Vi har inte fått någon bekräftelse på en betalning än. Om du har
        genomfört den slås videosamtal för {resident} på av sig självt — du kan
        stänga den här sidan.
      </p>
      <p className="card__note">
        Betala inte igen. Om ingenting har hänt inom en timme, kontakta{" "}
        {session.premiseName}.
      </p>
      <button
        className="btn btn--ghost"
        type="button"
        onClick={() => setWaiting(true)}
      >
        Kontrollera igen
      </button>
      <button className="btn btn--link" type="button" onClick={onDone}>
        Tillbaka
      </button>
    </section>
  );
}
